import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Edit, Trash2, ExternalLink } from "lucide-react";
import { z } from "zod";

const aiToolSchema = z.object({
  name: z.string().trim().min(1, "Tool name is required").max(100, "Tool name must be less than 100 characters"),
  description: z.string().trim().min(10, "Description must be at least 10 characters").max(1000, "Description must be less than 1000 characters"),
  use_case_tags: z.string().max(500, "Use cases must be less than 500 characters"),
  external_link: z.string().url("Invalid URL format")
});

interface AITool {
  id: string;
  name: string;
  description: string;
  use_case_tags: string[];
  external_link: string;
  logo_url?: string;
  created_at: string;
}

const isDev = import.meta.env.DEV;

export const AIToolsManager = () => {
  const [tools, setTools] = useState<AITool[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTool, setEditingTool] = useState<AITool | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    use_case_tags: "",
    external_link: ""
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);

  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = async () => {
    try {
      const { data, error } = await supabase.from("ai_tools").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      setTools(data || []);
    } catch (error) {
      if (isDev) console.error("Error loading AI tools:", error);
      toast({ title: "Error", description: "Failed to load AI tools", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Validate input data first
      const validatedData = aiToolSchema.parse(formData);
      
      const tagsArray = validatedData.use_case_tags.split(',').map(tag => tag.trim()).filter(tag => tag);

      // Upload logo if provided
      let logoUrl: string | undefined;
      if (logoFile) {
        const fileExt = logoFile.name.split('.').pop();
        const fileName = `${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('ai-tool-logos')
          .upload(fileName, logoFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('ai-tool-logos')
          .getPublicUrl(fileName);

        logoUrl = publicUrl;
      }

      const finalData = {
        name: validatedData.name,
        description: validatedData.description,
        use_case_tags: tagsArray,
        external_link: validatedData.external_link,
        ...(logoUrl && { logo_url: logoUrl })
      };

      if (editingTool) {
        const { error } = await supabase.from("ai_tools").update(finalData).eq("id", editingTool.id);
        if (error) throw error;
        toast({ title: "Success", description: "AI tool updated successfully" });
      } else {
        const { error } = await supabase.from("ai_tools").insert([finalData]);
        if (error) throw error;
        toast({ title: "Success", description: "AI tool created successfully" });
      }

      setIsDialogOpen(false);
      setEditingTool(null);
      setFormData({ name: "", description: "", use_case_tags: "", external_link: "" });
      setLogoFile(null);
      loadTools();
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({ 
          title: "Validation Error", 
          description: error.errors[0].message, 
          variant: "destructive" 
        });
      } else {
        if (isDev) console.error("Error saving AI tool:", error);
        toast({ title: "Error", description: "Failed to save AI tool", variant: "destructive" });
      }
    }
  };

  const handleDelete = async (tool: AITool) => {
    try {
      const { error } = await supabase.from("ai_tools").delete().eq("id", tool.id);
      if (error) throw error;
      
      toast({ title: "Success", description: "AI tool deleted successfully" });
      loadTools();
    } catch (error) {
      if (isDev) console.error("Error deleting AI tool:", error);
      toast({ title: "Error", description: "Failed to delete AI tool", variant: "destructive" });
    }
  };

  if (loading) return <div>Loading AI tools...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">AI Tools</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingTool(null); setFormData({ name: "", description: "", use_case_tags: "", external_link: "" }); setLogoFile(null); }}>
              <Plus className="w-4 h-4 mr-2" />Add AI Tool
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingTool ? "Edit AI Tool" : "Add New AI Tool"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Tool Name</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="use_case_tags">Use Cases (comma-separated)</Label>
                <Input id="use_case_tags" value={formData.use_case_tags} onChange={(e) => setFormData({ ...formData, use_case_tags: e.target.value })} placeholder="Content Creation, Email Marketing, Design" />
              </div>
              <div>
                <Label htmlFor="external_link">External Link</Label>
                <Input id="external_link" type="url" value={formData.external_link} onChange={(e) => setFormData({ ...formData, external_link: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="logo">Tool Logo (JPEG/PNG)</Label>
                <Input 
                  id="logo" 
                  type="file" 
                  accept=".jpeg,.jpg,.png" 
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)} 
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit">{editingTool ? "Update" : "Create"} Tool</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {tools.map((tool) => (
          <Card key={tool.id}>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                {tool.logo_url && (
                  <img 
                    src={tool.logo_url} 
                    alt={`${tool.name} logo`}
                    className="w-12 h-12 object-contain rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <CardTitle className="text-lg">{tool.name}</CardTitle>
                </div>
              </div>
              <CardDescription>{tool.description}</CardDescription>
              {tool.use_case_tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {tool.use_case_tags.map((tag, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              )}
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" asChild>
                  <a href={tool.external_link} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-1" />Visit
                  </a>
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditingTool(tool); setFormData({name: tool.name, description: tool.description, use_case_tags: tool.use_case_tags.join(', '), external_link: tool.external_link}); setLogoFile(null); setIsDialogOpen(true); }}>
                  <Edit className="w-4 h-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete AI Tool</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{tool.name}"? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(tool)}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};